/**
 * QuotationCreatePage — iter-292 (Feb 2026) — Full rewrite
 * ========================================================
 * Fixed sections (always on top, cannot reorder):
 *   1) Customer Information
 *   2) Quotation Details
 *
 * Dynamic sections (user adds with "+ Add Section", can reorder):
 *   - services (array of services with gov-fee not-included toggle)
 *   - package_name (bilingual package name)
 *   - terms (bilingual terms & conditions)
 *   - customer_notes (bilingual notes visible to the customer on PDF)
 *   - internal_notes (private, NEVER on PDF)
 *
 * Translation:
 *   - Global "ترجم كل الحقول" button at top → bulk-translates every
 *     non-empty bilingual pair where one side is missing.
 *   - Inline arrow button between every AR/EN pair → single-field
 *     translate.
 *
 * Save success → Modal with:
 *   - Quotation number
 *   - "طباعة PDF" (opens picker: AR / EN / Both)
 *   - "تحميل PDF"
 *   - "إدارة عروض الأسعار" (navigates to list)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Save, Loader2, Plus, Trash2, Printer, User, FileText, Package,
  Languages, ChevronUp, ChevronDown, Check, Info, Download, ListChecks,
  X, FileSignature, StickyNote, MessageSquare,
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';
import { printQuotation } from '../../utils/printQuotation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Section catalog ─── */
const SECTION_META = {
  services:        { ar: 'الخدمات (رسوم من - إلى)',   en: 'Services (range pricing)',      icon: Package },
  fixed_services:  { ar: 'خدمات برسوم ثابتة',           en: 'Services (fixed pricing)',      icon: Package },
  package_name:    { ar: 'اسم الباكج',                  en: 'Package Name',                  icon: FileSignature },
  terms:           { ar: 'الشروط والأحكام',             en: 'Terms & Conditions',            icon: FileText },
  customer_notes:  { ar: 'ملاحظات للعميل',              en: 'Customer Notes',                icon: MessageSquare },
  internal_notes:  { ar: 'ملاحظات داخلية',              en: 'Internal Notes',                icon: StickyNote },
};

const DEFAULT_TERMS_AR = `• هذا العرض ساري المفعول لمدة 30 يوماً من تاريخ إصداره.
• الرسوم الحكومية قابلة للتغيير حسب الجهات الرسمية.
• الدفع مقدم 50% عند الموافقة، والباقي قبل التسليم النهائي.
• المدة المذكورة تقديرية وتبدأ من يوم استلام كامل المستندات.`;

const EMPTY_SERVICE = (pricingType = 'range') => ({
  name_ar: '', name_en: '',
  description_ar: '', description_en: '',
  duration_ar: '', duration_en: '',
  // iter-298 — structured duration (number + type) overrides the free-text
  // duration_ar / duration_en when set. default duration_type is null so
  // the user must explicitly pick a chip once they enter a number.
  duration_days: null,
  duration_type: 'business',  // default to the common case; user can toggle
  pricing_type: pricingType,
  // iter-294 — per-service display mode. 'detailed' keeps the current
  // Gov + Service breakdown; 'combined' hides government fees and
  // writes the whole price into service_fee_* as a single amount.
  fee_mode: 'detailed',
  gov_fee_from: 0, gov_fee_to: 0,
  service_fee_from: 0, service_fee_to: 0,
  gov_fee_not_included: false,
});

// iter-298 — Derive the Arabic / English duration labels from the
// structured (days, type) pair. Returns null for both if days isn't
// set so legacy free-text duration_ar / duration_en stay authoritative.
const deriveDurationLabels = (days, type) => {
  const n = parseInt(days, 10);
  if (!n || n < 1) return { duration_ar: '', duration_en: '' };
  const isBusiness = type === 'business';
  const arUnit = isBusiness ? 'يوم عمل' : 'يوم';
  const enUnit = isBusiness
    ? (n === 1 ? 'business day' : 'business days')
    : (n === 1 ? 'day' : 'days');
  return { duration_ar: `${n} ${arUnit}`, duration_en: `${n} ${enUnit}` };
};

// iter-298 — Parse an existing free-text duration (from a quotation
// created before this iter) into {days, type} so the structured UI
// pre-fills correctly on edit.
const parseLegacyDuration = (s) => {
  if (!s) return { days: null, type: null };
  const str = String(s).trim();
  const num = str.match(/\d+/);
  if (!num) return { days: null, type: null };
  const days = parseInt(num[0], 10);
  const isBiz = /عمل|business/i.test(str);
  return { days, type: isBiz ? 'business' : 'calendar' };
};

/* ─── Translation helper — talks to the central auto_translation bot ─── */
const translateText = async (text, from, to) => {
  if (!text || !text.trim()) return '';
  const r = await axios.post(`${API}/utils/translate`,
    { text: text.trim(), source: from, target: to },
    { headers: getAuthHeaders() });
  return r.data?.translated || '';
};

export default function QuotationCreatePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [nextNo, setNextNo] = useState(null);

  // Full form state. `enabled_sections` is the authoritative ordered list;
  // sections not in it are not rendered and their data is still kept.
  const [form, setForm] = useState({
    customer_name: '', customer_company: '', customer_phone: '', customer_email: '',
    title_ar: '', title_en: '',
    valid_until: '', status: 'draft',
    services: [EMPTY_SERVICE()],
    package_name_ar: '', package_name_en: '',
    terms_ar: DEFAULT_TERMS_AR, terms_en: '',
    customer_notes_ar: '', customer_notes_en: '',
    notes: '',
    // iter-296 — enabled_sections no longer force the "services (range)"
    // section by default; the user chooses which sections to add just
    // like all other sections (terms / notes / package / fixed_services).
    enabled_sections: [],
  });

  // Save success modal state
  const [savedDoc, setSavedDoc] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);
  // Section picker modal
  const [pickerOpen, setPickerOpen] = useState(false);
  // Print mode picker
  const [printPickerOpen, setPrintPickerOpen] = useState(false);
  // iter-295 — true when the picker was opened from the "Download" button
  // so after mode selection we force an auto-download instead of a preview.
  const [downloadAfterPick, setDownloadAfterPick] = useState(false);

  /* ───── Load existing or peek number ───── */
  useEffect(() => {
    (async () => {
      try {
        if (isEdit) {
          const r = await axios.get(`${API}/admin/quotations/${id}`, { headers: getAuthHeaders() });
          const q = r.data.data;
          setForm((p) => ({
            ...p,
            customer_name:    q.customer_name    || '',
            customer_company: q.customer_company || '',
            customer_phone:   q.customer_phone   || '',
            customer_email:   q.customer_email   || '',
            title_ar:         q.title_ar         || '',
            title_en:         q.title_en         || '',
            valid_until:      q.valid_until      || '',
            status:           q.status           || 'draft',
            services:         (q.services && q.services.length) ? q.services.map((s) => {
              // Legacy duration back-fill — if we don't have the structured
              // (duration_days + duration_type) pair yet, derive it from
              // the free-text duration_ar/duration_en so the new UI shows
              // the right chip + number on edit.
              let dDays = s.duration_days;
              let dType = s.duration_type;
              if (dDays == null) {
                const parsed = parseLegacyDuration(s.duration_ar || s.duration_en || s.duration);
                if (parsed.days != null) { dDays = parsed.days; dType = parsed.type || 'business'; }
              }
              return {
                ...EMPTY_SERVICE(), ...s,
                description_ar: s.description_ar || s.description || '',
                duration_ar:    s.duration_ar    || s.duration    || '',
                duration_days:  dDays,
                duration_type:  dType || 'business',
                pricing_type:   s.pricing_type || 'range',
                fee_mode:       s.fee_mode || 'detailed',
              };
            }) : [EMPTY_SERVICE()],
            package_name_ar:  q.package_name_ar  || '',
            package_name_en:  q.package_name_en  || '',
            terms_ar:         q.terms_ar         || '',
            terms_en:         q.terms_en         || '',
            customer_notes_ar: q.customer_notes_ar || '',
            customer_notes_en: q.customer_notes_en || '',
            notes:            q.notes            || '',
            enabled_sections: (q.enabled_sections && q.enabled_sections.length)
              ? q.enabled_sections
              : ['services'],  // Legacy quotations get just services
          }));
          setNextNo(q.quotation_no);
          setSavedDoc(q);
        } else {
          const r = await axios.get(`${API}/admin/quotations/next-number`, { headers: getAuthHeaders() });
          setNextNo(r.data.data?.next_number);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message);
      } finally { setLoading(false); }
    })();
  }, [id, isEdit]);

  /* ───── Helpers ───── */
  const upd  = (patch)    => setForm((p) => ({ ...p, ...patch }));
  const updS = (i, patch) => setForm((p) => ({
    ...p, services: p.services.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
  }));
  // iter-293 — service helpers are PER pricing-type now. The underlying
  // `services` array is still a single list; UI operations filter it by
  // the row's `pricing_type` so the two sections manage disjoint rows.
  const addServiceOfType = (pricingType) =>
    upd({ services: [...form.services, EMPTY_SERVICE(pricingType)] });
  const delService = (i) => upd({ services: form.services.filter((_, idx) => idx !== i) });

  const addSection = (secId) => {
    if (form.enabled_sections.includes(secId)) {
      toast.info(t('هذا القسم مُضاف بالفعل', 'This section is already added'));
      setPickerOpen(false);
      return;
    }
    upd({ enabled_sections: [...form.enabled_sections, secId] });
    setPickerOpen(false);
  };
  const removeSection = (secId) => {
    const next = { enabled_sections: form.enabled_sections.filter((s) => s !== secId) };
    // iter-296 — When a pricing section is removed, drop its services so
    // orphan rows don't resurface in the PDF or the summary totals.
    if (secId === 'services') {
      next.services = form.services.filter((s) => s.pricing_type === 'fixed');
    } else if (secId === 'fixed_services') {
      next.services = form.services.filter((s) => (s.pricing_type || 'range') === 'range');
    }
    upd(next);
  };
  const moveSection = (secId, delta) => {
    const list = [...form.enabled_sections];
    const idx = list.indexOf(secId);
    const target = idx + delta;
    if (idx < 0 || target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    upd({ enabled_sections: list });
  };

  /* ───── Totals preview ───── */
  const totals = useMemo(() => {
    const servicesWithFlag = form.services.map((s) => ({
      ...s,
      _gov_fee_from: s.gov_fee_not_included ? 0 : Number(s.gov_fee_from || 0),
      _gov_fee_to:   s.gov_fee_not_included ? 0 : Number(s.gov_fee_to   || 0),
    }));
    const gf1 = servicesWithFlag.reduce((a, s) => a + s._gov_fee_from, 0);
    const gf2 = servicesWithFlag.reduce((a, s) => a + s._gov_fee_to,   0);
    const sf1 = form.services.reduce((a, s) => a + Number(s.service_fee_from || 0), 0);
    const sf2 = form.services.reduce((a, s) => a + Number(s.service_fee_to   || 0), 0);
    const hasExcluded = form.services.some((s) => s.gov_fee_not_included);
    return { gf1, gf2, sf1, sf2, tot1: gf1 + sf1, tot2: gf2 + sf2, hasExcluded };
  }, [form.services]);

  /* ───── Translation ───── */
  const translateOne = async (arKey, enKey, scope = form) => {
    try {
      const ar = scope[arKey]; const en = scope[enKey];
      if (!ar && !en) {
        toast.error(t('اكتب في أحد الحقلين أولاً', 'Fill one of the two fields first'));
        return;
      }
      if (ar && !en) {
        const out = await translateText(ar, 'ar', 'en');
        upd({ [enKey]: out });
      } else if (en && !ar) {
        const out = await translateText(en, 'en', 'ar');
        upd({ [arKey]: out });
      } else {
        // Both filled — translate AR → EN (overwrite EN)
        const out = await translateText(ar, 'ar', 'en');
        upd({ [enKey]: out });
      }
      toast.success(t('تمت الترجمة', 'Translated'));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const translateService = async (i, arKey, enKey) => {
    const s = form.services[i];
    try {
      if (!s[arKey] && !s[enKey]) {
        toast.error(t('اكتب في أحد الحقلين أولاً', 'Fill one of the two fields first')); return;
      }
      if (s[arKey] && !s[enKey]) {
        updS(i, { [enKey]: await translateText(s[arKey], 'ar', 'en') });
      } else if (s[enKey] && !s[arKey]) {
        updS(i, { [arKey]: await translateText(s[enKey], 'en', 'ar') });
      } else {
        updS(i, { [enKey]: await translateText(s[arKey], 'ar', 'en') });
      }
      toast.success(t('تمت الترجمة', 'Translated'));
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  /** Bulk translate every empty English/Arabic counterpart. */
  const translateAll = async () => {
    setTranslating(true);
    try {
      const PAIRS = [
        ['title_ar', 'title_en'],
        ['package_name_ar', 'package_name_en'],
        ['terms_ar', 'terms_en'],
        ['customer_notes_ar', 'customer_notes_en'],
      ];
      const next = { ...form };
      let count = 0;
      for (const [a, e] of PAIRS) {
        if (next[a] && !next[e]) {
          next[e] = await translateText(next[a], 'ar', 'en'); count++;
        } else if (next[e] && !next[a]) {
          next[a] = await translateText(next[e], 'en', 'ar'); count++;
        }
      }
      // Services
      next.services = await Promise.all(next.services.map(async (s) => {
        const r = { ...s };
        for (const [a, e] of [['name_ar', 'name_en'], ['description_ar', 'description_en'], ['duration_ar', 'duration_en']]) {
          if (r[a] && !r[e])      { r[e] = await translateText(r[a], 'ar', 'en'); count++; }
          else if (r[e] && !r[a]) { r[a] = await translateText(r[e], 'en', 'ar'); count++; }
        }
        return r;
      }));
      setForm(next);
      toast.success(t(`تمت ترجمة ${count} حقل`, `Translated ${count} field${count !== 1 ? 's' : ''}`));
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setTranslating(false); }
  };

  /* ───── Save ───── */
  const save = async () => {
    if (!form.customer_name.trim()) {
      toast.error(t('اسم العميل مطلوب', 'Customer name is required')); return;
    }
    // iter-294 — validate per-section: a section is required to have at
    // least one filled service only when THAT section is enabled.
    const rangeFilled = form.services.some((s) =>
      (s.pricing_type || 'range') === 'range' && (s.name_ar.trim() || s.name_en.trim()));
    const fixedFilled = form.services.some((s) =>
      s.pricing_type === 'fixed' && (s.name_ar.trim() || s.name_en.trim()));
    const rangeNeeded = form.enabled_sections.includes('services');
    const fixedNeeded = form.enabled_sections.includes('fixed_services');
    if (rangeNeeded && fixedNeeded && !rangeFilled && !fixedFilled) {
      toast.error(t('أضف خدمة واحدة على الأقل في أحد القسمين',
                    'Add at least one service in either section')); return;
    }
    if (rangeNeeded && !fixedNeeded && !rangeFilled) {
      toast.error(t('أضف خدمة واحدة على الأقل في قسم رسوم من - إلى',
                    'Add at least one service in the range pricing section')); return;
    }
    if (!rangeNeeded && fixedNeeded && !fixedFilled) {
      toast.error(t('أضف خدمة واحدة على الأقل في قسم الرسوم الثابتة',
                    'Add at least one service in the fixed pricing section')); return;
    }

    // iter-295 — Bilingual parity: every AR field must have its EN
    // counterpart filled and vice-versa. We only apply the check to
    // sections the user actually enabled, and we only check pairs where
    // AT LEAST one side is non-empty (both-empty is always allowed).
    const bothOrNone = (ar, en) => {
      const a = (ar || '').trim();
      const e = (en || '').trim();
      return (!a && !e) || (!!a && !!e);
    };
    const sameLineCount = (ar, en) => {
      const a = (ar || '').trim();
      const e = (en || '').trim();
      if (!a && !e) return true;
      if (!a || !e) return false;
      const splitLines = (s) => s.split(/\n+/).map((l) => l.trim()).filter(Boolean);
      return splitLines(a).length === splitLines(e).length;
    };

    // Title parity (free, always-visible fields — skip if both empty)
    if (!bothOrNone(form.title_ar, form.title_en)) {
      toast.error(t('عنوان العرض يجب أن يكون بالعربي والإنجليزي معاً',
                    'Quotation title must be in both Arabic AND English')); return;
    }

    // Service parity — one-by-one so the user knows which row is incomplete
    for (let i = 0; i < form.services.length; i++) {
      const s = form.services[i];
      const hasAny = (s.name_ar || '').trim() || (s.name_en || '').trim()
                   || (s.description_ar || '').trim() || (s.description_en || '').trim()
                   || (s.duration_ar || '').trim() || (s.duration_en || '').trim();
      if (!hasAny) continue; // blank row — it will be filtered out on save
      if (!bothOrNone(s.name_ar, s.name_en)) {
        toast.error(t(`الخدمة #${i + 1}: اسم الخدمة ناقص بإحدى اللغتين`,
                      `Service #${i + 1}: service name is missing in one language`)); return;
      }
      if (!bothOrNone(s.description_ar, s.description_en)) {
        toast.error(t(`الخدمة #${i + 1}: الوصف ناقص بإحدى اللغتين`,
                      `Service #${i + 1}: description is missing in one language`)); return;
      }
      if (!bothOrNone(s.duration_ar, s.duration_en)) {
        toast.error(t(`الخدمة #${i + 1}: المدة ناقصة بإحدى اللغتين`,
                      `Service #${i + 1}: duration is missing in one language`)); return;
      }
    }

    if (form.enabled_sections.includes('package_name')
        && !bothOrNone(form.package_name_ar, form.package_name_en)) {
      toast.error(t('اسم الباكج يجب أن يكون بالعربي والإنجليزي معاً',
                    'Package name must be in both Arabic AND English')); return;
    }
    if (form.enabled_sections.includes('terms')
        && !sameLineCount(form.terms_ar, form.terms_en)) {
      toast.error(t('الشروط والأحكام: عدد النقاط بالعربي والإنجليزي يجب أن يكون متساوياً',
                    'Terms & Conditions: Arabic and English must have the same number of lines')); return;
    }
    if (form.enabled_sections.includes('customer_notes')
        && !bothOrNone(form.customer_notes_ar, form.customer_notes_en)) {
      toast.error(t('ملاحظات العميل يجب أن تكون بالعربي والإنجليزي معاً',
                    'Customer notes must be in both Arabic AND English')); return;
    }
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.valid_until) delete body.valid_until;
      // iter-294 — drop empty service rows (default EMPTY_SERVICE() seeds
      // one blank 'range' row that must not be posted when the user only
      // filled a different section). Keep rows that have a name in EITHER
      // language. Legacy duration/description are forwarded for back-compat.
      body.services = body.services
        .filter((s) => ((s.name_ar || '').trim() || (s.name_en || '').trim()))
        .map((s) => ({
          ...s,
          description: s.description_ar || s.description || '',
          duration:    s.duration_ar    || s.duration    || '',
        }));
      const url = isEdit
        ? `${API}/admin/quotations/${id}`
        : `${API}/admin/quotations`;
      const method = isEdit ? 'put' : 'post';
      const r = await axios[method](url, body, { headers: getAuthHeaders() });
      setSavedDoc(r.data.data);
      setSuccessOpen(true);
      if (!isEdit) {
        navigate(`/manage/cloudgm-fz-llc/quotations/edit/${r.data.data.id}`, { replace: true });
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      // iter-294 — Pydantic 422 returns a list of validation errors;
      // surface the first one in plain language so the user knows which
      // field is wrong instead of a generic "status code 422".
      let msg;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        const d = detail[0];
        const field = Array.isArray(d.loc) ? d.loc.slice(1).join('.') : '';
        msg = field ? `${field}: ${d.msg}` : d.msg;
      } else {
        msg = e.message || 'Save failed';
      }
      toast.error(msg);
    } finally { setSaving(false); }
  };

  /* ───── Print / Download ───── */
  const triggerPrint = (mode) => {
    if (!savedDoc) return;
    printQuotation(savedDoc, { language, mode, download: downloadAfterPick });
    setPrintPickerOpen(false);
    setDownloadAfterPick(false);
  };

  const breadcrumb = useMemo(() => ([
    { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
    { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/dashboard' },
    { labelAr: 'إدارة شركة CLOUDGM FZ LLC', labelEn: 'Management', path: '/manage/cloudgm-fz-llc' },
    { labelAr: 'عروض الأسعار', labelEn: 'Quotations', path: '/manage/cloudgm-fz-llc/quotations' },
    { labelAr: isEdit ? 'تعديل' : 'إنشاء', labelEn: isEdit ? 'Edit' : 'Create' },
  ]), [isEdit]);

  /* ──────── RENDER ──────── */
  return (
    <DashboardLayout
      title={isEdit ? 'تعديل عرض سعر' : 'إنشاء عرض سعر جديد'}
      titleEn={isEdit ? 'Edit Quotation' : 'Create New Quotation'}
      subtitle={nextNo ? `رقم العرض: ${nextNo}` : ''}
      subtitleEn={nextNo ? `Quotation No.: ${nextNo}` : ''}
      backPath="/manage/cloudgm-fz-llc/quotations/list"
      onBack={() => navigate('/manage/cloudgm-fz-llc/quotations/list')}
      breadcrumb={breadcrumb}
    >
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="quotation-create-page">
        {loading ? (
          <div className="h-60 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-white/40" />
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">

            {/* ── Translate All button ── */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-white/50">
                {t('اكتب بالعربي، واضغط الزر لترجمة كل الحقول للإنجليزية تلقائياً.',
                   'Write in Arabic, then click the button to auto-translate every field to English.')}
              </p>
              <Button onClick={translateAll} disabled={translating} size="sm"
                      className="bg-gradient-to-r from-[#3B9EDC] to-[#F5A55A] text-white font-semibold border-0"
                      data-testid="translate-all-btn">
                {translating ? <Loader2 className="h-3.5 w-3.5 me-1.5 animate-spin" />
                             : <Languages className="h-3.5 w-3.5 me-1.5" />}
                {t('ترجم كل الحقول', 'Translate all fields')}
              </Button>
            </div>

            {/* ── 1) Customer section (FIXED) ── */}
            <Panel title={t('بيانات العميل', 'Customer Information')} icon={User} locked>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t('اسم العميل *', 'Customer name *')}
                       value={form.customer_name} onChange={(v) => upd({ customer_name: v })}
                       testId="field-customer-name" />
                <Field label={t('اسم الشركة', 'Company name')}
                       value={form.customer_company} onChange={(v) => upd({ customer_company: v })}
                       testId="field-customer-company" />
                <Field label={t('الهاتف', 'Phone')} dir="ltr"
                       value={form.customer_phone} onChange={(v) => upd({ customer_phone: v })}
                       testId="field-customer-phone" />
                <Field label={t('البريد الإلكتروني', 'Email')} dir="ltr"
                       value={form.customer_email} onChange={(v) => upd({ customer_email: v })}
                       testId="field-customer-email" />
              </div>
            </Panel>

            {/* ── 2) Quotation Details (FIXED) ── */}
            <Panel title={t('بيانات العرض', 'Quotation Details')} icon={FileText} locked>
              <div className="space-y-4">
                <BilingualPair
                  labelAr={t('عنوان العرض (عربي)', 'Title (Arabic)')}
                  labelEn={t('عنوان العرض (إنجليزي)', 'Title (English)')}
                  valueAr={form.title_ar} valueEn={form.title_en}
                  onChangeAr={(v) => upd({ title_ar: v })}
                  onChangeEn={(v) => upd({ title_en: v })}
                  onTranslate={() => translateOne('title_ar', 'title_en')}
                  testIdAr="field-title-ar" testIdEn="field-title-en"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] text-white/60">{t('ساري حتى', 'Valid until')}</Label>
                    <Input type="date" value={form.valid_until || ''}
                           onChange={(e) => upd({ valid_until: e.target.value })}
                           className="bg-black/30 border-white/10 text-sm"
                           data-testid="field-valid-until" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] text-white/60">{t('الحالة', 'Status')}</Label>
                    <select value={form.status}
                            onChange={(e) => upd({ status: e.target.value })}
                            className="w-full bg-black/30 border border-white/10 rounded-md px-3 h-10 text-sm text-white"
                            data-testid="field-status">
                      <option value="draft">{t('مسودة', 'Draft')}</option>
                      <option value="sent">{t('مرسل', 'Sent')}</option>
                      <option value="accepted">{t('مقبول', 'Accepted')}</option>
                      <option value="rejected">{t('مرفوض', 'Rejected')}</option>
                      <option value="expired">{t('منتهي', 'Expired')}</option>
                    </select>
                  </div>
                </div>
              </div>
            </Panel>

            {/* ── Dynamic sections area ── */}
            <AnimatePresence>
              {form.enabled_sections.map((secId, idx) => {
                const meta = SECTION_META[secId];
                if (!meta) return null;
                const first = idx === 0;
                const last  = idx === form.enabled_sections.length - 1;
                const title = isAr ? meta.ar : meta.en;

                return (
                  <motion.div key={secId}
                    initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                    <Panel
                      title={title} icon={meta.icon}
                      action={(
                        <div className="flex items-center gap-1" data-testid={`section-actions-${secId}`}>
                          <IconBtn disabled={first} onClick={() => moveSection(secId, -1)}
                                   icon={ChevronUp} title={t('حرّك لأعلى', 'Move up')}
                                   testId={`section-up-${secId}`} />
                          <IconBtn disabled={last} onClick={() => moveSection(secId, +1)}
                                   icon={ChevronDown} title={t('حرّك لأسفل', 'Move down')}
                                   testId={`section-down-${secId}`} />
                          <IconBtn onClick={() => removeSection(secId)}
                                   icon={Trash2} title={t('حذف القسم', 'Remove section')}
                                   className="hover:bg-red-500/10 hover:text-red-400"
                                   testId={`section-remove-${secId}`} />
                        </div>
                      )}>

                      {secId === 'services' && (
                        <ServicesEditor form={form} updS={updS}
                                         addService={() => addServiceOfType('range')}
                                         delService={delService} translateService={translateService}
                                         pricingType="range"
                                         isAr={isAr} t={t} />
                      )}

                      {secId === 'fixed_services' && (
                        <ServicesEditor form={form} updS={updS}
                                         addService={() => addServiceOfType('fixed')}
                                         delService={delService} translateService={translateService}
                                         pricingType="fixed"
                                         isAr={isAr} t={t} />
                      )}

                      {secId === 'package_name' && (
                        <BilingualPair
                          labelAr={t('اسم الباكج (عربي)', 'Package name (Arabic)')}
                          labelEn={t('اسم الباكج (إنجليزي)', 'Package name (English)')}
                          valueAr={form.package_name_ar} valueEn={form.package_name_en}
                          onChangeAr={(v) => upd({ package_name_ar: v })}
                          onChangeEn={(v) => upd({ package_name_en: v })}
                          onTranslate={() => translateOne('package_name_ar', 'package_name_en')}
                          testIdAr="field-package-ar" testIdEn="field-package-en"
                        />
                      )}

                      {secId === 'terms' && (
                        <BilingualPair
                          multiline rows={8}
                          labelAr={t('الشروط والأحكام (عربي)', 'Terms & Conditions (Arabic)')}
                          labelEn={t('الشروط والأحكام (إنجليزي)', 'Terms & Conditions (English)')}
                          valueAr={form.terms_ar} valueEn={form.terms_en}
                          onChangeAr={(v) => upd({ terms_ar: v })}
                          onChangeEn={(v) => upd({ terms_en: v })}
                          onTranslate={() => translateOne('terms_ar', 'terms_en')}
                          testIdAr="field-terms-ar" testIdEn="field-terms-en"
                        />
                      )}

                      {secId === 'customer_notes' && (
                        <BilingualPair
                          multiline rows={4}
                          labelAr={t('ملاحظات للعميل (عربي)', 'Notes to customer (Arabic)')}
                          labelEn={t('ملاحظات للعميل (إنجليزي)', 'Notes to customer (English)')}
                          valueAr={form.customer_notes_ar} valueEn={form.customer_notes_en}
                          onChangeAr={(v) => upd({ customer_notes_ar: v })}
                          onChangeEn={(v) => upd({ customer_notes_en: v })}
                          onTranslate={() => translateOne('customer_notes_ar', 'customer_notes_en')}
                          testIdAr="field-cust-notes-ar" testIdEn="field-cust-notes-en"
                        />
                      )}

                      {secId === 'internal_notes' && (
                        <div className="space-y-1.5">
                          <Label className="text-[11px] text-white/60">
                            {t('ملاحظات داخلية (لا تظهر في PDF)', 'Internal notes (NOT shown on PDF)')}
                          </Label>
                          <Textarea rows={3} value={form.notes}
                                    onChange={(e) => upd({ notes: e.target.value })}
                                    dir={isAr ? 'rtl' : 'ltr'}
                                    className="bg-black/30 border-white/10 text-sm"
                                    data-testid="field-notes" />
                        </div>
                      )}

                    </Panel>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {/* ── Totals preview (shows when ANY services section is active) ── */}
            {(form.enabled_sections.includes('services') || form.enabled_sections.includes('fixed_services')) && (
              <div className="rounded-xl bg-gradient-to-r from-[#3B9EDC]/[0.08] to-[#F5A55A]/[0.05] border border-[#3B9EDC]/20 p-4"
                   data-testid="totals-preview">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <TotalCell label={t('الرسوم الحكومية', 'Government fees')}
                             min={totals.gf1} max={totals.gf2} tint="#F5A55A" />
                  <TotalCell label={t('رسوم الخدمة', 'Service fees')}
                             min={totals.sf1} max={totals.sf2} tint="#3B9EDC" />
                  <TotalCell label={t('الإجمالي', 'Grand total')}
                             min={totals.tot1} max={totals.tot2} tint="#10b981" bold />
                </div>
                {totals.hasExcluded && (
                  <p className="mt-2 text-[11px] text-amber-300 flex items-center gap-1.5">
                    <Info className="h-3 w-3" />
                    {t('بعض الخدمات: الرسوم الحكومية غير متضمنة في عرض السعر.',
                       'Some services: government fees NOT included in this quotation.')}
                  </p>
                )}
              </div>
            )}

            {/* ── + Add Section button ── */}
            <button onClick={() => setPickerOpen(true)}
                    className="w-full py-4 rounded-2xl border-2 border-dashed border-white/15 hover:border-[#F5A55A]/50 hover:bg-[#F5A55A]/[0.03] transition flex items-center justify-center gap-2 text-white/60 hover:text-[#F5A55A]"
                    data-testid="add-section-btn">
              <Plus className="h-4 w-4" />
              <span className="text-sm font-semibold">
                {t('إضافة قسم', 'Add section')}
              </span>
            </button>

            {/* ── Save bar (fixed at the end of the form, non-sticky) ── */}
            <div className="mt-6" data-testid="save-bar">
              <div className="bg-black/80 backdrop-blur-xl border border-white/10 rounded-2xl p-3 flex items-center justify-between gap-3">
                <div className="text-xs text-white/50">
                  {isEdit ? t('التعديلات تُحفظ على نفس رقم العرض.', 'Changes save under the same quotation number.')
                          : t('سيتم إصدار رقم العرض عند الحفظ.', 'The quotation number will be issued on first save.')}
                </div>
                <Button onClick={save} disabled={saving}
                        className="bg-[#10b981] hover:bg-[#059669] text-black font-semibold"
                        data-testid="btn-save-quotation">
                  {saving ? <Loader2 className="h-3.5 w-3.5 me-1.5 animate-spin" />
                          : <Save className="h-3.5 w-3.5 me-1.5" />}
                  {t('حفظ', 'Save')}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </section>

      {/* ─── Section picker modal ─── */}
      <AnimatePresence>
        {pickerOpen && (
          <Modal onClose={() => setPickerOpen(false)} testId="section-picker-modal"
                 title={t('إضافة قسم', 'Add Section')}>
            <div className="space-y-2">
              {Object.entries(SECTION_META).map(([secId, meta]) => {
                const added = form.enabled_sections.includes(secId);
                const Icon = meta.icon;
                return (
                  <button key={secId}
                          onClick={() => !added && addSection(secId)}
                          disabled={added}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition text-start ${
                            added
                              ? 'bg-white/[0.02] border-white/10 opacity-50 cursor-not-allowed'
                              : 'bg-white/[0.03] border-white/10 hover:border-[#F5A55A]/50 hover:bg-[#F5A55A]/[0.05]'
                          }`}
                          data-testid={`picker-${secId}`}>
                    <div className="h-9 w-9 rounded-xl bg-[#F5A55A]/15 border border-[#F5A55A]/30 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-[#F5A55A]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white">{isAr ? meta.ar : meta.en}</p>
                    </div>
                    {added && <Check className="h-4 w-4 text-emerald-400" />}
                  </button>
                );
              })}
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* ─── Save Success modal ─── */}
      <AnimatePresence>
        {successOpen && savedDoc && (
          <Modal onClose={() => setSuccessOpen(false)} testId="save-success-modal"
                 title={t('تم الحفظ بنجاح', 'Saved Successfully')}
                 titleColor="#10b981" iconComponent={Check}>
            <div className="text-center space-y-4 py-3">
              <div className="h-16 w-16 rounded-full bg-emerald-500/15 border-2 border-emerald-500/40 flex items-center justify-center mx-auto">
                <Check className="h-8 w-8 text-emerald-400" />
              </div>
              <p className="text-white/80 text-sm">
                {t('تم حفظ عرض السعر برقم', 'Your quotation has been saved as')}
              </p>
              <p className="font-mono font-bold text-[#F5A55A] text-xl" dir="ltr" data-testid="success-quot-no">
                {savedDoc.quotation_no}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2">
                <Button onClick={() => setPrintPickerOpen(true)}
                        className="bg-white/5 hover:bg-white/10 text-white border border-white/10"
                        data-testid="success-print-btn">
                  <Printer className="h-3.5 w-3.5 me-1.5" />
                  {t('طباعة', 'Print')}
                </Button>
                <Button onClick={() => { setPrintPickerOpen(true); setDownloadAfterPick(true); }}
                        className="bg-white/5 hover:bg-white/10 text-white border border-white/10"
                        data-testid="success-download-btn">
                  <Download className="h-3.5 w-3.5 me-1.5" />
                  {t('تحميل PDF', 'Download PDF')}
                </Button>
                <Button onClick={() => navigate('/manage/cloudgm-fz-llc/quotations/list')}
                        className="bg-[#3B9EDC] hover:bg-[#2a7eb8] text-white font-semibold"
                        data-testid="success-manage-btn">
                  <ListChecks className="h-3.5 w-3.5 me-1.5" />
                  {t('إدارة العروض', 'Manage')}
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* ─── Print mode picker modal ─── */}
      <AnimatePresence>
        {printPickerOpen && (
          <Modal onClose={() => { setPrintPickerOpen(false); setDownloadAfterPick(false); }} testId="print-mode-modal"
                 title={downloadAfterPick ? t('اختر لغة الملف للتحميل', 'Choose Download Language')
                                          : t('اختر لغة الطباعة', 'Choose Print Language')}>
            <div className="space-y-2">
              <PrintModeBtn mode="ar"   label="عربي فقط"       sublabel="Arabic only"     onClick={triggerPrint} />
              <PrintModeBtn mode="en"   label="إنجليزي فقط"    sublabel="English only"    onClick={triggerPrint} />
              <PrintModeBtn mode="both" label="عربي + إنجليزي" sublabel="Both side-by-side"
                             onClick={triggerPrint} highlighted />
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
}

/* ──────────── PRIMITIVES ──────────── */

const Panel = ({ title, icon: Icon, action, locked, children }) => (
  <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5"
           data-testid={`panel-${(title || '').trim().slice(0, 20)}`}>
    <div className="flex items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-[#3B9EDC]" />}
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        {locked && <span className="text-[9px] text-white/30 ms-1">(ثابت / fixed)</span>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const Field = ({ label, value, onChange, placeholder, dir, testId }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] text-white/60">{label}</Label>
    <Input value={value || ''} onChange={(e) => onChange(e.target.value)}
           placeholder={placeholder || ''} dir={dir}
           className="bg-black/30 border-white/10 text-sm"
           data-testid={testId} />
  </div>
);

/** Bilingual field pair (AR + EN) with an inline translate button
 *  between them. `multiline` switches to Textarea.                   */
const BilingualPair = ({
  labelAr, labelEn, valueAr, valueEn, onChangeAr, onChangeEn,
  onTranslate, multiline, rows = 3, testIdAr, testIdEn,
}) => {
  const InputEl = multiline ? Textarea : Input;
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-end">
      <div className="space-y-1.5">
        <Label className="text-[11px] text-white/60">{labelAr}</Label>
        <InputEl value={valueAr || ''} onChange={(e) => onChangeAr(e.target.value)}
                 dir="rtl" rows={multiline ? rows : undefined}
                 className="bg-black/30 border-white/10 text-sm"
                 data-testid={testIdAr} />
      </div>
      <button type="button" onClick={onTranslate}
              className="h-9 w-9 rounded-full bg-gradient-to-br from-[#3B9EDC]/15 to-[#F5A55A]/15 border border-[#3B9EDC]/30 hover:scale-110 transition flex items-center justify-center mb-1"
              title="Translate"
              data-testid={`translate-${testIdAr || testIdEn}`}>
        <Languages className="h-4 w-4 text-[#F5A55A]" />
      </button>
      <div className="space-y-1.5">
        <Label className="text-[11px] text-white/60">{labelEn}</Label>
        <InputEl value={valueEn || ''} onChange={(e) => onChangeEn(e.target.value)}
                 dir="ltr" rows={multiline ? rows : undefined}
                 className="bg-black/30 border-white/10 text-sm"
                 data-testid={testIdEn} />
      </div>
    </div>
  );
};

const IconBtn = ({ icon: Icon, onClick, disabled, title, className = '', testId }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title}
          className={`h-7 w-7 rounded-md flex items-center justify-center transition ${
            disabled ? 'text-white/20 cursor-not-allowed' : 'text-white/60 hover:bg-white/5 hover:text-white'
          } ${className}`}
          data-testid={testId}>
    <Icon className="h-3.5 w-3.5" />
  </button>
);

const Modal = ({ title, titleColor = '#F5A55A', iconComponent: Icon, onClose, children, testId }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={onClose}>
    <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
                className="bg-[#0a0a0a] border rounded-2xl p-5 w-full max-w-md max-h-[85vh] overflow-y-auto"
                style={{ borderColor: `${titleColor}30` }}
                onClick={(e) => e.stopPropagation()}
                data-testid={testId}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4" style={{ color: titleColor }} />}
          <h3 className="text-base font-bold text-white">{title}</h3>
        </div>
        <button onClick={onClose} className="text-white/50 hover:text-white"
                data-testid={`${testId}-close`}>
          <X className="h-4 w-4" />
        </button>
      </div>
      {children}
    </motion.div>
  </motion.div>
);

const PrintModeBtn = ({ mode, label, sublabel, onClick, highlighted }) => (
  <button onClick={() => onClick(mode)}
          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition text-start ${
            highlighted
              ? 'bg-[#F5A55A]/[0.08] border-[#F5A55A]/40 hover:bg-[#F5A55A]/[0.15]'
              : 'bg-white/[0.03] border-white/10 hover:border-white/30 hover:bg-white/[0.05]'
          }`}
          data-testid={`print-mode-${mode}`}>
    <Printer className={`h-5 w-5 ${highlighted ? 'text-[#F5A55A]' : 'text-white/60'}`} />
    <div className="flex-1 text-start">
      <p className="text-sm font-semibold text-white">{label}</p>
      <p className="text-[10px] text-white/40">{sublabel}</p>
    </div>
  </button>
);

const TotalCell = ({ label, min, max, tint, bold }) => {
  const minStr = Number(min || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
  const maxStr = Number(max || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
  return (
    <div>
      <p className="text-[10px] uppercase text-white/50 tracking-wider mb-1">{label}</p>
      <p className={`${bold ? 'text-base font-bold' : 'text-sm'} tabular-nums`}
         style={{ color: tint }} dir="ltr">
        {minStr === maxStr ? maxStr : `${minStr} - ${maxStr}`}
        <span className="text-[10px] text-white/40 ms-1">AED</span>
      </p>
    </div>
  );
};

/** Services editor broken out as a sub-component to keep the main
 *  render tree readable. Handles the gov-fee-not-included toggle AND
 *  the two pricing modes (`range` from-to, or `fixed` single amount).
 *
 *  Props:
 *    pricingType: 'range' | 'fixed'  — filters services, controls UI.
 */
const ServicesEditor = ({ form, updS, addService, delService, translateService,
                           pricingType, isAr, t }) => {
  // Build (globalIndex, service) pairs so delete/update ops still target
  // the right row inside the master `form.services` array even though
  // we only render a filtered subset here.
  const rows = form.services
    .map((s, i) => ({ i, s }))
    .filter(({ s }) => (s.pricing_type || 'range') === pricingType);

  const singleInput = (globalIdx, label, value, onChange, testId) => (
    <div className="space-y-1.5">
      <Label className="text-[11px] text-white/60">{label}</Label>
      <Input type="number" step="0.01" min="0" dir="ltr"
             value={value}
             onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
             className="bg-black/30 border-white/10 text-sm"
             data-testid={testId} />
    </div>
  );

  return (
    <>
      <div className="space-y-4" data-testid={`services-list-${pricingType}`}>
        {rows.length === 0 && (
          <div className="rounded-lg border border-dashed border-white/10 bg-black/20 p-4 text-center text-xs text-white/40">
            {t('اضغط "إضافة خدمة" لإضافة أول خدمة.',
               'Press "Add service" to add the first service.')}
          </div>
        )}

        {rows.map(({ i, s }) => (
          <div key={i} className="bg-black/20 rounded-xl p-4 border border-white/5"
               data-testid={`service-row-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase text-white/50">
                {t(`الخدمة ${rows.findIndex((r) => r.i === i) + 1}`,
                   `Service ${rows.findIndex((r) => r.i === i) + 1}`)}
              </p>
              <IconBtn onClick={() => delService(i)} icon={Trash2}
                       className="hover:bg-red-500/10 hover:text-red-400"
                       testId={`service-del-${i}`}
                       title={t('حذف', 'Delete')} />
            </div>
            <div className="space-y-3">
              {/* iter-294 — Fee display mode toggle (detailed vs combined) */}
              <div className="flex items-center justify-between gap-2 flex-wrap bg-black/20 border border-white/5 rounded-lg px-3 py-2"
                   data-testid={`service-${i}-fee-mode-row`}>
                <Label className="text-[11px] text-white/60 whitespace-nowrap">
                  {t('نمط عرض الرسوم:', 'Fee display:')}
                </Label>
                <div className="flex rounded-md bg-black/40 border border-white/10 p-0.5" dir="ltr">
                  <button
                    type="button"
                    onClick={() => updS(i, { fee_mode: 'detailed' })}
                    className={`px-3 py-1 text-[11px] rounded transition-colors ${
                      (s.fee_mode || 'detailed') === 'detailed'
                        ? 'bg-[#3B9EDC] text-white font-semibold'
                        : 'text-white/60 hover:text-white'
                    }`}
                    data-testid={`service-${i}-fee-mode-detailed`}
                  >
                    {t('تفصيلي (حكومية + خدمة)', 'Detailed (Gov + Service)')}
                  </button>
                  <button
                    type="button"
                    onClick={() => updS(i, {
                      fee_mode: 'combined',
                      gov_fee_from: 0, gov_fee_to: 0,
                      gov_fee_not_included: false,
                    })}
                    className={`px-3 py-1 text-[11px] rounded transition-colors ${
                      s.fee_mode === 'combined'
                        ? 'bg-[#F5A55A] text-white font-semibold'
                        : 'text-white/60 hover:text-white'
                    }`}
                    data-testid={`service-${i}-fee-mode-combined`}
                  >
                    {t('مبلغ شامل', 'Combined amount')}
                  </button>
                </div>
              </div>

              {/* Name AR / EN */}
              <BilingualPair
                labelAr={t('اسم الخدمة (عربي) *', 'Name (Arabic) *')}
                labelEn={t('اسم الخدمة (إنجليزي)', 'Name (English)')}
                valueAr={s.name_ar} valueEn={s.name_en}
                onChangeAr={(v) => updS(i, { name_ar: v })}
                onChangeEn={(v) => updS(i, { name_en: v })}
                onTranslate={() => translateService(i, 'name_ar', 'name_en')}
                testIdAr={`service-${i}-name-ar`} testIdEn={`service-${i}-name-en`}
              />
              {/* iter-298 — Duration: number + chip (business / calendar).
                   Writes duration_days + duration_type AND auto-derives
                   duration_ar/en strings for the PDF to pick up. */}
              <div className="rounded-lg bg-black/20 border border-white/5 p-3 flex flex-wrap items-center gap-3"
                   data-testid={`service-${i}-duration-block`}>
                <Label className="text-[11px] text-white/60 whitespace-nowrap">
                  {t('المدة', 'Duration')}
                </Label>
                <Input
                  type="number"
                  min="1"
                  placeholder={t('مثال: 30', 'e.g., 30')}
                  value={s.duration_days ?? ''}
                  onChange={(e) => {
                    const n = e.target.value === '' ? null : Math.max(1, parseInt(e.target.value, 10) || 0);
                    const labels = deriveDurationLabels(n, s.duration_type || 'business');
                    updS(i, { duration_days: n, ...labels });
                  }}
                  className="h-9 w-28 bg-black/40 border-white/10 text-white text-sm"
                  dir="ltr"
                  data-testid={`service-${i}-duration-days`}
                />
                <div className="flex rounded-md bg-black/40 border border-white/10 p-0.5" dir="ltr">
                  {[
                    { v: 'business', labelAr: 'يوم عمل',   labelEn: 'Business day' },
                    { v: 'calendar', labelAr: 'يوم',       labelEn: 'Calendar day' },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => {
                        const labels = deriveDurationLabels(s.duration_days, o.v);
                        updS(i, { duration_type: o.v, ...labels });
                      }}
                      className={`px-3 py-1 text-[11px] rounded transition-colors ${
                        (s.duration_type || 'business') === o.v
                          ? (o.v === 'business' ? 'bg-[#3B9EDC] text-white font-semibold'
                                                : 'bg-[#F5A55A] text-white font-semibold')
                          : 'text-white/60 hover:text-white'
                      }`}
                      data-testid={`service-${i}-duration-type-${o.v}`}
                    >
                      {t(o.labelAr, o.labelEn)}
                    </button>
                  ))}
                </div>
                {/* Live preview of the resulting phrase */}
                {s.duration_days ? (
                  <div className="text-[11px] text-white/50 flex gap-3 ms-auto" dir="auto">
                    <span data-testid={`service-${i}-dur-preview-ar`} style={{ fontFamily: 'Cairo' }}>
                      {s.duration_ar}
                    </span>
                    <span className="text-white/30">·</span>
                    <span data-testid={`service-${i}-dur-preview-en`} dir="ltr">
                      {s.duration_en}
                    </span>
                  </div>
                ) : null}
              </div>
              {/* Government fee + not-included toggle — HIDDEN when combined */}
              {s.fee_mode !== 'combined' && (
              <div className="rounded-lg bg-black/30 border border-white/5 p-3"
                   data-testid={`service-${i}-gov-block`}>
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <Label className="text-[11px] text-white/60">
                    {t('الرسوم الحكومية (AED)', 'Government fees (AED)')}
                  </Label>
                  <label className="flex items-center gap-2 cursor-pointer text-[11px] text-white/70"
                         data-testid={`service-${i}-govtoggle-label`}>
                    <input type="checkbox" checked={!!s.gov_fee_not_included}
                           onChange={(e) => updS(i, { gov_fee_not_included: e.target.checked })}
                           className="h-4 w-4 rounded border-white/20 bg-black/40 text-[#F5A55A] focus:ring-[#F5A55A]"
                           data-testid={`service-${i}-govtoggle`} />
                    {t('غير متضمنة في العرض', 'Not included in quotation')}
                  </label>
                </div>
                {s.gov_fee_not_included ? (
                  <div className="p-3 rounded-lg bg-amber-500/[0.08] border border-amber-500/30 flex items-start gap-2"
                       data-testid={`service-${i}-govnote`}>
                    <Info className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />
                    <p className="text-[11px] text-amber-200 leading-relaxed">
                      {t('الرسوم الحكومية لهذه الخدمة غير متضمنة في عرض السعر. سيتم إشعار العميل بأن القيمة تُدفع مباشرة للجهة الحكومية.',
                         'Government fees for this service are NOT included in the quotation. The customer will be informed to pay the authority directly.')}
                    </p>
                  </div>
                ) : pricingType === 'fixed' ? (
                  singleInput(
                    i,
                    t('المبلغ (AED)', 'Amount (AED)'),
                    s.gov_fee_to,
                    (v) => updS(i, { gov_fee_from: v, gov_fee_to: v }),
                    `service-${i}-gov-fixed`,
                  )
                ) : (
                  <div className="flex items-center gap-1">
                    <Input type="number" step="0.01" min="0" dir="ltr"
                           value={s.gov_fee_from}
                           onChange={(e) => updS(i, { gov_fee_from: parseFloat(e.target.value) || 0 })}
                           placeholder="From"
                           className="bg-black/30 border-white/10 text-sm"
                           data-testid={`service-${i}-gov-from`} />
                    <span className="text-white/40 text-xs">↔</span>
                    <Input type="number" step="0.01" min="0" dir="ltr"
                           value={s.gov_fee_to}
                           onChange={(e) => updS(i, { gov_fee_to: parseFloat(e.target.value) || 0 })}
                           placeholder="To"
                           className="bg-black/30 border-white/10 text-sm"
                           data-testid={`service-${i}-gov-to`} />
                  </div>
                )}
              </div>
              )}
              {/* Service fee — label becomes "Fees (combined)" when combined mode */}
              <div className="space-y-1.5">
                <Label className="text-[11px] text-white/60">
                  {s.fee_mode === 'combined'
                    ? t('الرسوم الشاملة (AED)', 'Combined fees (AED)')
                    : t('رسوم الخدمة (AED)', 'Service fees (AED)')}
                </Label>
                {pricingType === 'fixed' ? (
                  <Input type="number" step="0.01" min="0" dir="ltr"
                         value={s.service_fee_to}
                         onChange={(e) => {
                           const v = parseFloat(e.target.value) || 0;
                           updS(i, { service_fee_from: v, service_fee_to: v });
                         }}
                         className="bg-black/30 border-white/10 text-sm"
                         data-testid={`service-${i}-svc-fixed`} />
                ) : (
                  <div className="flex items-center gap-1">
                    <Input type="number" step="0.01" min="0" dir="ltr"
                           value={s.service_fee_from}
                           onChange={(e) => updS(i, { service_fee_from: parseFloat(e.target.value) || 0 })}
                           placeholder="From"
                           className="bg-black/30 border-white/10 text-sm"
                           data-testid={`service-${i}-svc-from`} />
                    <span className="text-white/40 text-xs">↔</span>
                    <Input type="number" step="0.01" min="0" dir="ltr"
                           value={s.service_fee_to}
                           onChange={(e) => updS(i, { service_fee_to: parseFloat(e.target.value) || 0 })}
                           placeholder="To"
                           className="bg-black/30 border-white/10 text-sm"
                           data-testid={`service-${i}-svc-to`} />
                  </div>
                )}
              </div>
              {/* Description */}
              <BilingualPair
                multiline rows={2}
                labelAr={t('وصف الخدمة (عربي)', 'Description (Arabic)')}
                labelEn={t('وصف الخدمة (إنجليزي)', 'Description (English)')}
                valueAr={s.description_ar} valueEn={s.description_en}
                onChangeAr={(v) => updS(i, { description_ar: v })}
                onChangeEn={(v) => updS(i, { description_en: v })}
                onTranslate={() => translateService(i, 'description_ar', 'description_en')}
                testIdAr={`service-${i}-desc-ar`} testIdEn={`service-${i}-desc-en`}
              />
            </div>
          </div>
        ))}
      </div>
      <Button size="sm" onClick={addService}
              className="mt-3 bg-[#10b981] hover:bg-[#059669] text-black font-semibold"
              data-testid={`add-service-btn-${pricingType}`}>
        <Plus className="h-3.5 w-3.5 me-1" /> {t('إضافة خدمة', 'Add service')}
      </Button>
    </>
  );
};
